import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { useCMS } from '../store/cmsStore';

export default function Hotels() {
  const { data } = useCMS();
  const [activeTab, setActiveTab] = useState(0);
  const [currentImage, setCurrentImage] = useState(0);

  const hotelCategories = data.hotels.filter(hotel => hotel.active).sort((a, b) => a.order - b.order);

  if (hotelCategories.length === 0) {
    return null;
  }

  const currentHotel = hotelCategories[activeTab] || hotelCategories[0];

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % currentHotel.images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + currentHotel.images.length) % currentHotel.images.length);
  };

  return (
    <section id="hotels" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 md:mb-12">
          <span className="inline-block bg-gold-100 text-gold-700 px-4 py-2 rounded-full text-sm font-cairo font-medium mb-4">
            إقامتك
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-amiri font-bold text-gray-900 mb-4">
            فنادقنا المختارة
          </h2>
          <p className="text-gray-600 font-cairo">
            من 3 نجوم إلى 5 نجوم — اختر ما يناسب راحتك وميزانيتك
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-3 md:gap-4 mb-8 md:mb-12 overflow-x-auto pb-2">
          {hotelCategories.map((hotel, index) => (
            <button
              key={hotel.id}
              onClick={() => {
                setActiveTab(index);
                setCurrentImage(0);
              }}
              className={`px-6 md:px-8 py-3 md:py-4 rounded-xl font-cairo font-bold text-base md:text-lg transition-all flex-shrink-0 ${
                activeTab === index
                  ? 'bg-gold-500 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                {hotel.label}
                <span className="flex gap-0.5">
                  {Array.from({ length: hotel.stars }).map((_, i) => (
                    <Star key={i} size={14} fill="currentColor" />
                  ))}
                </span>
              </span>
            </button>
          ))}
        </div>

        {/* Image Gallery */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="relative">
            <img
              src={currentHotel.images[currentImage]}
              alt={currentHotel.label}
              className="w-full h-64 md:h-96 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 800 400"><rect fill="%23f3f4f6" width="800" height="400"/><text fill="%239ca3af" x="400" y="200" dominant-baseline="middle" text-anchor="middle" font-size="24" font-family="Arial">صورة غير متوفرة</text></svg>';
              }}
            />
            
            {/* Navigation Arrows */}
            {currentHotel.images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition"
                >
                  <ChevronRight className="w-6 h-6 text-gray-800" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-800" />
                </button>
              </>
            )}

            {/* Badge */}
            <div className="absolute top-4 right-4 bg-gold-500 text-white px-4 py-2 rounded-full font-cairo font-bold text-sm">
              {currentHotel.badge}
            </div>

            {/* Dots */}
            {currentHotel.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {currentHotel.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImage(index)}
                    className={`w-3 h-3 rounded-full transition ${
                      currentImage === index ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}